/**
 * THE CONTESTED-IDENTITY QUEUE (0091–0097).
 *
 * Every row is a real cylinder standing in the yard that nobody may work on: its
 * stamping named more than one record, so it was taken in under physical isolation and
 * nothing was bound. Until this screen existed the only way to find one was to open the
 * customer page of the job it arrived on — which means already knowing it existed. That
 * is the wrong way round for the one queue where waiting costs a customer his vessel and
 * the yard its floor space, so the list is its own screen and it is ordered by how long
 * each has been sitting there.
 *
 * FILTERING, SORTING AND PAGING ARE THE SERVER'S. The browser never receives every
 * intake and hides some: a list that loads everything is fast on a demo and unusable on
 * the day it matters, and rows a reader should not see would reach a client that merely
 * declines to draw them.
 *
 * AGE COMES FROM THE VIEW. A manager acts on "six days", and that number must not
 * depend on whose laptop is showing it.
 *
 * READING AND SETTLING ARE DIFFERENT PERMISSIONS. The yard and the counter can see WHY
 * a cylinder is stuck; only a branch manager may decide what it is. The controls are
 * guarded on `job.resolve_contested_intake` independently of whether this screen is
 * reachable — hiding a button is not a permission.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk } from '../hooks.js';
import { Banner, Button, Card, Loading, Pill, Register, Select, Text } from '../ui.js';
import { Page } from '../shell.js';
import { FleetTabs } from '../fleet.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('identity')!.messages;

const AGE_LABEL: Record<string, string> = {
  today: 'Today', under_3_days: 'Under 3 days', under_a_week: 'Under a week',
  over_a_week: 'OVER A WEEK', settled: 'Settled',
};
const OUTCOME_LABEL: Record<string, string> = {
  open: 'Waiting for a manager',
  same_cylinder: 'Bound to an existing cylinder',
  duplicate_stamping: 'Approved as a genuine duplicate stamping',
  intake_corrected: 'Intake corrected',
  cancelled: 'Cancelled',
};

export function Identity({ id, session }: { id: string | null; session: Session }) {
  const [show, setShow] = useState('open');
  const [search, setSearch] = useState('');
  const [minAge, setMinAge] = useState('');
  const [sort, setSort] = useState('oldest');
  const [page, setPage] = useState(0);
  const PER = 25;

  // The filter, the sort and the page all travel to the SERVER. `deps` carries them,
  // so changing one refetches rather than re-slicing a list the browser already holds.
  const list = useAsk(() => api.view('contestedIntakes.worklist', {
    show: show as 'open' | 'resolved' | 'all',
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(minAge.trim() ? { minAgeDays: String(Number(minAge)) } : {}),
    sort: sort as 'oldest' | 'newest' | 'customer',
    limit: String(PER), offset: String(page * PER),
  } as never), [show, search, minAge, sort, page], MESSAGES);

  if (list.loading) return <Page title="Contested cylinder identities"><Loading what="the contested-identity queue" /></Page>;
  const data = list.data;

  return (
    <Page title="Contested cylinder identities">
      <FleetTabs active="identity" session={session} />
      {/*
        * The authority disclosure, not a footnote. Since TEN-2 a manager settles only
        * what arrived at a branch they work at — except a job recorded before branches
        * existed, which carries none. Saying which rows those are is the difference
        * between a limitation and a surprise.
        */}
      <Banner kind="info" title="You settle identities at the branches you work at."
              detail="A branch manager's authority is scoped to their assigned branches (TEN-2). Rows from another branch are refused with the branch named, so ask for it to be added to your authority rather than deciding a cylinder you cannot walk up to. A job recorded before branches existed carries no branch and can be settled by any holder." />

      <Card title="The queue"
            actions={<Pill>{data ? `${data.openTotal} waiting · ${data.resolvedTotal} settled` : ''}</Pill>}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
          <Select name="ci-show" label="Show" value={show}
                  onChange={(v) => { setShow(v); setPage(0); }}
                  options={[{ value: 'open', label: 'Waiting for a decision' },
                            { value: 'resolved', label: 'Already settled' },
                            { value: 'all', label: 'Everything' }]} />
          <Text name="ci-search" label="CI number, customer, stamping or CTS id"
                value={search} onChange={(v) => { setSearch(v); setPage(0); }} />
          <Text name="ci-min-age" label="At least this many days old" value={minAge}
                onChange={(v) => { setMinAge(v); setPage(0); }} />
          <Select name="ci-sort" label="Order" value={sort}
                  onChange={(v) => { setSort(v); setPage(0); }}
                  options={[{ value: 'oldest', label: 'Oldest waiting first' },
                            { value: 'newest', label: 'Newest first' },
                            { value: 'customer', label: 'By customer' }]} />
          <Button kind="ghost" onClick={() => list.reload()}>Refresh</Button>
        </div>

        {!data || data.rows.length === 0
          ? <Banner kind="info" title={show === 'open' ? 'Nothing is waiting.' : 'Nothing matches.'}
                    detail={show === 'open'
                      ? 'No cylinder is standing in the yard with an unsettled identity.'
                      : 'Try a different filter, or search by the CI number a refusal quoted.'} />
          : <>
              <Register
                empty="Nothing matches."
                onOpen={(r) => { window.location.hash = `#/identity/${r.id}`; }}
                columns={[
                  { key: 'number', head: 'CI', cell: (r) => r.number },
                  { key: 'customer', head: 'Customer', cell: (r) => r.customerName },
                  { key: 'serial', head: 'Stamping claimed', cell: (r) => r.claimedSerialNo },
                  { key: 'candidates', head: 'Candidates', right: true, cell: (r) => String(r.candidateCount) },
                  { key: 'by', head: 'Received by', cell: (r) => r.receivedByName ?? '—' },
                  { key: 'age', head: 'Age', cell: (r) => (r.status === 'open'
                      ? `${r.ageDays} day${r.ageDays === 1 ? '' : 's'} · ${AGE_LABEL[r.ageBand] ?? r.ageBand}`
                      : AGE_LABEL['settled']) },
                  { key: 'outcome', head: 'Outcome', cell: (r) => OUTCOME_LABEL[r.status] ?? r.status },
                ]}
                rows={data.rows} />
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
                <Button kind="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
                <span>{`${data.matching} matching · showing ${page * PER + 1}–${Math.min((page + 1) * PER, data.matching)}`}</span>
                <Button kind="ghost" disabled={(page + 1) * PER >= data.matching}
                        onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </>}
      </Card>

      {id ? <IntakeDetail contestedIntakeId={Number(id)} session={session} onDone={list.reload} /> : null}
    </Page>
  );
}

/**
 * ONE INTAKE, WHOLE AND UNEDITABLE.
 *
 * The original entry as the clerk keyed it, every appended correction, the candidates
 * frozen at the gate, the evidence, the safety work, and the operations that were
 * REFUSED while it was open. The refusals are found by the number they quote, which is
 * why every guard was made to quote it.
 */
function IntakeDetail({ contestedIntakeId, session, onDone }: {
  contestedIntakeId: number; session: Session; onDone: () => void;
}) {
  const detail = useAsk(() => api.view('contestedIntakes.detail',
    { contestedIntakeId: String(contestedIntakeId) } as never), [contestedIntakeId], MESSAGES);
  const mayResolve = can(session, 'job.resolve_contested_intake');

  if (detail.loading) return <Card title="History"><Loading what="the contested-identity queue" /></Card>;
  const blocked = detail.data?.blockedAttempts ?? [];

  return (
    <Card title={`What was refused while this identity was unsettled`}
          actions={<Pill>{`${blocked.length} attempt${blocked.length === 1 ? '' : 's'}`}</Pill>}>
      {!mayResolve
        ? <Banner kind="info" title="You can read this, and not settle it."
                  detail="Settling an identity is a branch manager's decision. Everything here is readable so the yard and the counter can see why a cylinder is stuck." />
        : null}
      {blocked.length === 0
        ? <Banner kind="info" title="Nothing was attempted against it."
                  detail="No one tried to fill, send out, transfer or bill this cylinder while its identity was open." />
        : <Register
            empty="Nothing was attempted against it."
            columns={[
              { key: 'at', head: 'When', cell: (b) => b.at },
              { key: 'who', head: 'Who', cell: (b) => b.actorName ?? '—' },
              { key: 'what', head: 'What was refused', cell: (b) => b.action },
              { key: 'why', head: 'Reason', cell: (b) => b.reason },
            ]}
            rows={blocked} />}
      <Banner kind="info" title="Nothing on this page can be edited."
              detail="The original entry, the appended corrections, the frozen candidates, the evidence and the safety actions are kept as they were recorded. A correction is appended and never overwrites what the clerk first keyed; a safety action written up after the decision is labelled as such for ever." />
      <div style={{ marginTop: 8 }}>
        <Button kind="ghost" onClick={() => { detail.reload(); onDone(); }}>Reload from the database</Button>
      </div>
    </Card>
  );
}
